import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Lock, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) setError('No reset token provided. Click the link in your email again.');
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (pw !== pw2)    { toast.error('Passwords do not match'); return; }
    setSubmitting(true);
    setError(null);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pw });
      setDone(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Reset failed.  The link may have expired.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f8f9ff] p-4" style={{ fontFamily: "'Inter', sans-serif" }}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8 border border-[#c3c6d7]/30" data-testid="reset-password-page">
        {!done ? (
          <>
            <h1 className="text-2xl font-extrabold text-[#0b1c30] tracking-tight mb-1">Set a new password</h1>
            <p className="text-sm text-[#434655] mb-6">Enter a new password for your Sovereign IQ account. Minimum 8 characters.</p>
            {error && (
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg mb-4" data-testid="reset-error">
                <AlertTriangle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                <span className="text-[12px] text-red-700">{error}</span>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#434655] font-semibold mb-1 block">New password *</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#c3c6d7]" />
                  <input
                    type="password" required minLength={8}
                    value={pw}
                    onChange={e => setPw(e.target.value)}
                    data-testid="reset-pw1-input"
                    className="w-full bg-[#f8f9ff] border border-[#c3c6d7]/40 rounded-lg pl-10 pr-4 py-2.5 text-sm text-[#0b1c30] focus:ring-1 focus:ring-[#004ac6] outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#434655] font-semibold mb-1 block">Confirm password *</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#c3c6d7]" />
                  <input
                    type="password" required minLength={8}
                    value={pw2}
                    onChange={e => setPw2(e.target.value)}
                    data-testid="reset-pw2-input"
                    className="w-full bg-[#f8f9ff] border border-[#c3c6d7]/40 rounded-lg pl-10 pr-4 py-2.5 text-sm text-[#0b1c30] focus:ring-1 focus:ring-[#004ac6] outline-none"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={submitting || !token}
                data-testid="reset-submit-btn"
                className="w-full bg-gradient-to-r from-[#004ac6] to-[#7c3aed] text-white py-3 rounded-lg text-sm font-bold hover:shadow-lg transition-all disabled:opacity-50">
                {submitting ? 'Updating…' : 'Update password'}
              </button>
            </form>
            <div className="text-center mt-6">
              <Link to="/login" className="text-[11px] font-bold text-[#434655] hover:text-[#004ac6]">Back to sign in</Link>
            </div>
          </>
        ) : (
          <div className="text-center py-4" data-testid="reset-success">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
            <h1 className="text-xl font-extrabold text-[#0b1c30] tracking-tight mb-2">Password updated</h1>
            <p className="text-sm text-[#434655] mb-4">Your password has been changed. Redirecting to sign-in…</p>
          </div>
        )}
      </div>
    </div>
  );
}
